import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Icon, IconName, LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Subtitle } from "capitalrx-components-2.0/lib/typography";
import styled from "styled-components";

import { ExternalConnectionLink } from "@/App/api/externalConnections";
import { ClientAwareLink, ThemedButton } from "@/App/components";
import { ReactComponent as CostcoLogo } from "@/App/components/MemberCard/Assets/costco-rx-logo.svg";
import { SearchParamKeys } from "@/App/constants/constants";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClientNewTab } from "@/App/hooks";
import { useListCategorizedExternalConnectionList } from "@/App/hooks/externalConnections";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

const PharmacyDetailTile = styled.div`
  border-radius: 8px;
  border: 1px solid #cfd9e1;
  padding: 24px;
  height: 100%;
  width: 100%;
`;

export const PharmacyPanel: FC = () => {
  const { t: translate } = useTranslation();

  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { hideHomeDeliveryCard, hideSpecialtyCard, hideCostcoHomeDeliveryCard, hideCostcoSpecialtyCard } = useContext(ExperienceContext);

  const navigate = useNavigateClientNewTab();

  const { externalConnectionsGeneral, isLoading: isLoadingExternalConnections } = useListCategorizedExternalConnectionList({
    location: window.location.href,
  });

  const { subPanelColumns, halfWidthColumns, isMobile } = useResponsive({
    isMobile: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
    subPanelColumns: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
    halfWidthColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
  });

  const infoSectionColumns = useMemo(() => {
    return 12 - ((subPanelColumns * (externalConnectionsGeneral?.length || 0)) % 12);
  }, [subPanelColumns, externalConnectionsGeneral]);

  const onExternalConnection = useCallback((link: ExternalConnectionLink) => {
    logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_external_link", { path: location });
    const urlParams = new URLSearchParams();
    if (isViewingDataholder) {
      urlParams.set(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
    }
    const toValue = `${location.protocol}//${location.host}/connection/${link.id}`;
    navigate(toValue, urlParams);
  }, []);

  return (
    <LoadingGroup loading={isLoadingExternalConnections}>
      <LoadingGroup.Placeholder
        fallback={
          <Shelf>
            <Shelf.Spacer />
            <Spinner size="large" />
            <Shelf.Spacer />
          </Shelf>
        }
      >
        <Grid>
          {(externalConnectionsGeneral || []).map((externalConnection) => {
            const link = externalConnection.link;

            return (
              <Grid.Span columns={subPanelColumns} key={"external-link-" + link.id}>
                <PharmacyDetailTile>
                  <Stack alignment={isMobile ? "span" : "left"}>
                    {!isMobile && (
                      <>
                        <Subtitle testId={`link-${link.id}-label`}>{link.label}</Subtitle>
                        <Stack.Spacer />
                        <Icon
                          testId={`link-${link.id}-icon`}
                          className="pharmacy-panel-icon"
                          name={link.dashboard_icon as IconName}
                          size="regular"
                        />
                      </>
                    )}
                    {isMobile && (
                      <Shelf>
                        <Icon
                          testId={`link-${link.id}-icon`}
                          className="pharmacy-panel-icon"
                          name={link.dashboard_icon as IconName}
                          size="regular"
                        />
                        <Subtitle testId={`link-${link.id}-label`}>{link.label}</Subtitle>
                      </Shelf>
                    )}
                    <Stack.Spacer />
                    <Paragraph testId={`link-${link.id}-helper-text`}>{link.helper_text}</Paragraph>
                    <Stack.Spacer />
                    <ThemedButton
                      testId={`link-${link.id}-external-connection`}
                      icon="link-out"
                      appearance="secondary"
                      onClick={() => {
                        onExternalConnection(link);
                      }}
                    >
                      {link.link_text}
                    </ThemedButton>
                    <Stack.Spacer />
                  </Stack>
                </PharmacyDetailTile>
              </Grid.Span>
            );
          })}
          {externalConnectionsGeneral?.length && (
            <Grid.Span columns={infoSectionColumns}>
              <PharmacyDetailTile>
                <Stack alignment="left">
                  <Paragraph>{translate("DASHBOARD.pharmacyPanel.getStarted")}</Paragraph>
                  <Shelf>
                    <Icon name="phone" size="regular" color="lightgrey" />
                    <Body>
                      {translate("DASHBOARD.pharmacyPanel.contactProviderToTransferPrescriptionsText")}{" "}
                      <ClientAwareLink to="/forms-and-documents">{translate("DASHBOARD.pharmacyPanel.formsAndDocuments")}</ClientAwareLink>
                    </Body>
                  </Shelf>
                  <Shelf>
                    <Icon name="placeholder" size="regular" color="lightgrey" />
                    <Body>{translate("DASHBOARD.pharmacyPanel.visitPharmacyText")}</Body>
                  </Shelf>
                  <Shelf>
                    <Icon name="pill-bottle" size="regular" color="lightgrey" />
                    <Body>{translate("DASHBOARD.pharmacyPanel.visitPharmacyInstructions")}</Body>
                  </Shelf>
                </Stack>
              </PharmacyDetailTile>
            </Grid.Span>
          )}
          {!externalConnectionsGeneral?.length && !hideCostcoHomeDeliveryCard && (
            <Grid.Span columns={halfWidthColumns}>
              <PharmacyDetailTile data-testid="div-test__mail-order-pharmacy-info">
                <Stack alignment={isMobile ? "span" : "left"} style={{ height: "100%" }}>
                  {!isMobile && (
                    <>
                      <Shelf alignment="center" spacing="small">
                        <Subtitle>Mail Order with</Subtitle>
                        <CostcoLogo height="24" width="80" viewBox="0 0 277 81" />
                      </Shelf>
                      <Stack.Spacer />
                    </>
                  )}
                  {isMobile && (
                    <Stack alignment="left" spacing="small">
                      <Subtitle>Mail Order with</Subtitle>
                      <CostcoLogo height="18" width="60" viewBox="0 0 277 81" />
                    </Stack>
                  )}
                  <Stack.Spacer />
                  <Paragraph>
                    Fill and refill your prescriptions online, 24 hours a day, and have them delivered anywhere in the United States
                  </Paragraph>
                  <ThemedButton
                    testId="costco-mail-order-external-connection"
                    icon="link-out"
                    appearance="secondary"
                    onClick={() => {
                      logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_external_link", { path: location });
                      window.open("https://rx.costco.com", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Visit Costco Pharmacy
                  </ThemedButton>
                </Stack>
              </PharmacyDetailTile>
            </Grid.Span>
          )}
          {!externalConnectionsGeneral?.length && hideCostcoHomeDeliveryCard && !hideHomeDeliveryCard && (
            <Grid.Span columns={halfWidthColumns}>
              <PharmacyDetailTile data-testid="div-test__mail-order-pharmacy-info">
                <Stack>
                  <Subtitle>{translate("DASHBOARD.pharmacyPanel.mailOrderPharmacy")}</Subtitle>
                  <Icon className="pharmacy-panel-icon" name="shipping" size="regular" />
                  <div>
                    {translate("DASHBOARD.pharmacyPanel.mailOrderSeeInformationText")}{" "}
                    <ClientAwareLink to="/forms-and-documents" testId="mail-order-pharmacy-info-forms-and-docs">
                      {translate("DASHBOARD.pharmacyPanel.formsAndDocumentsPage")}
                    </ClientAwareLink>
                  </div>
                  <Stack.Spacer />
                </Stack>
              </PharmacyDetailTile>
            </Grid.Span>
          )}
          {!externalConnectionsGeneral?.length && !hideCostcoSpecialtyCard && (
            <Grid.Span columns={halfWidthColumns}>
              <PharmacyDetailTile data-testid="div-test__specialty-pharmacy-info">
                <Stack alignment={isMobile ? "span" : "left"} style={{ height: "100%" }}>
                  {!isMobile && (
                    <>
                      <Shelf alignment="center" spacing="small">
                        <Subtitle>Specialty Service with</Subtitle>
                        <CostcoLogo height="24" width="80" viewBox="0 0 277 81" />
                      </Shelf>
                      <Stack.Spacer />
                    </>
                  )}
                  {isMobile && (
                    <Stack alignment="left" spacing="small">
                      <Subtitle>Specialty Service with</Subtitle>
                      <CostcoLogo height="18" width="60" viewBox="0 0 277 81" />
                    </Stack>
                  )}
                  <Stack.Spacer />
                  <Paragraph>Convenient, discreet and free next-day or second-day delivery for your specialty medications</Paragraph>
                  <Stack.Spacer />
                  <ThemedButton
                    testId="costco-specialty-external-connection"
                    icon="link-out"
                    appearance="secondary"
                    onClick={() => {
                      logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_external_link", { path: location });
                      window.open("https://www.lumicera.com/costco-specialty-pharmacy/", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Visit Costco Specialty Pharmacy
                  </ThemedButton>
                </Stack>
              </PharmacyDetailTile>
            </Grid.Span>
          )}
          {!externalConnectionsGeneral?.length && hideCostcoSpecialtyCard && !hideSpecialtyCard && (
            <Grid.Span columns={halfWidthColumns}>
              <PharmacyDetailTile data-testid="div-test__specialty-pharmacy-info">
                <Stack>
                  <Subtitle>{translate("DASHBOARD.pharmacyPanel.specialtyPharmacy")}</Subtitle>
                  <Icon className="pharmacy-panel-icon" name="pill-bottle" size="regular" />
                  <div>
                    {translate("DASHBOARD.pharmacyPanel.specialtyPharmacySeeInformationText")}{" "}
                    <ClientAwareLink to="/forms-and-documents" testId="specialty-pharmacy-info-forms-and-docs">
                      {translate("DASHBOARD.pharmacyPanel.formsAndDocumentsPage")}
                    </ClientAwareLink>
                  </div>
                  <Stack.Spacer />
                </Stack>
              </PharmacyDetailTile>
            </Grid.Span>
          )}
        </Grid>
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
